import React, { useState } from 'react';
import {
  Box, Typography, Chip, Button, Stack, Collapse,
  CircularProgress, Divider, Dialog, DialogTitle,
  DialogContent, DialogActions, Switch, Alert,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';

const CAMPAIGN_TYPE_COLORS = {
  marketing:      'primary',
  follow_up:      'info',
  reminder:       'warning',
  sales_outreach: 'success',
  reactivation:   'secondary',
};

const STATUS_CONFIG = {
  pending_approval: { label: 'Pending Approval', color: 'warning' },
  approved:         { label: 'Approved',          color: 'success' },
  rejected:         { label: 'Rejected',           color: 'error'   },
  converted:        { label: 'Converted to Campaign', color: 'success' },
};

export default function CampaignRecommendationCard({ recommendation: rec, onApprove, onReject, onConvert }) {
  const [loading, setLoading]           = useState(false);
  const [previewOpen, setPreviewOpen]   = useState(false);
  const [convertOpen, setConvertOpen]   = useState(false);
  const [personalised, setPersonalised] = useState(false);

  const handle = async (fn) => {
    if (typeof fn !== 'function') return;
    setLoading(true);
    try { await fn(); } finally { setLoading(false); }
  };

  const handleConfirmConvert = async () => {
    setConvertOpen(false);
    await handle(() => onConvert(personalised ? 'ai_generated' : 'none'));
  };

  const cfg = STATUS_CONFIG[rec.status] || STATUS_CONFIG.pending_approval;

  return (
    <Box sx={{ p: 2.5 }}>
      <Stack direction="row" spacing={1} alignItems="center" mb={1}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
          AI Campaign Recommendation
        </Typography>
        <Chip label={cfg.label} size="small" color={cfg.color} />
        {rec.is_ai_generated && <Chip label="Gemini" size="small" variant="outlined" sx={{ fontSize: 10 }} />}
      </Stack>

      {/* Theme + type */}
      <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
        <Typography variant="subtitle1" fontWeight={700}>{rec.content_theme}</Typography>
        <Chip
          label={rec.campaign_type?.replace('_', ' ')}
          size="small"
          color={CAMPAIGN_TYPE_COLORS[rec.campaign_type] || 'default'}
        />
        <Chip label={`${rec.confidence_score}% confidence`} size="small" variant="outlined" />
      </Stack>

      {/* Subject */}
      <Box sx={{ bgcolor: '#fff', border: '1px solid #e0e0e0', borderRadius: 1, px: 2, py: 1, mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary">SUGGESTED SUBJECT</Typography>
        <Typography variant="body2" fontWeight={600}>{rec.suggested_subject}</Typography>
      </Box>

      {/* Meta row */}
      <Stack direction="row" spacing={3} mb={1.5}>
        {rec.suggested_send_date && (
          <Box>
            <Typography variant="caption" color="text.secondary">SEND DATE</Typography>
            <Typography variant="body2">
              {new Date(rec.suggested_send_date).toLocaleDateString('en-GB', {
                weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
              })}
            </Typography>
          </Box>
        )}
        <Box>
          <Typography variant="caption" color="text.secondary">WHY THIS</Typography>
          <Typography variant="body2" sx={{ fontStyle: 'italic' }}>{rec.reason_for_recommendation}</Typography>
        </Box>
      </Stack>

      {/* Email preview toggle */}
      {rec.draft_body_html && (
        <>
          <Button
            size="small"
            variant="text"
            onClick={() => setPreviewOpen((p) => !p)}
            endIcon={previewOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ mb: 1, px: 0 }}
          >
            {previewOpen ? 'Hide' : 'Preview'} Email Draft
          </Button>
          <Collapse in={previewOpen}>
            <Box
              sx={{
                border: '1px solid #e0e0e0', borderRadius: 1,
                maxHeight: 400, overflowY: 'auto', mb: 1.5, bgcolor: '#fff',
              }}
            >
              <iframe
                srcDoc={rec.draft_body_html}
                title="Email Draft Preview"
                style={{ width: '100%', minHeight: 380, border: 'none' }}
                sandbox="allow-same-origin"
              />
            </Box>
          </Collapse>
        </>
      )}

      <Divider sx={{ my: 1.5 }} />

      {/* Actions */}
      {rec.status === 'pending_approval' && (
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            color="success"
            size="small"
            startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <CheckCircleIcon />}
            onClick={() => handle(onApprove)}
            disabled={loading}
          >
            Approve
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="small"
            startIcon={<CancelIcon />}
            onClick={() => handle(onReject)}
            disabled={loading}
          >
            Reject
          </Button>
        </Stack>
      )}

      {rec.status === 'approved' && (
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <RocketLaunchIcon />}
            onClick={() => setConvertOpen(true)}
            disabled={loading}
          >
            Convert to Campaign
          </Button>
        </Stack>
      )}

      {rec.status === 'converted' && rec.email_campaign_id && (
        <Typography variant="body2" color="success.main" fontWeight={600}>
          Campaign draft created (ID: {rec.email_campaign_id}) — find it in Mass Email to review and schedule.
        </Typography>
      )}

      {/* Convert dialog */}
      <Dialog open={convertOpen} onClose={() => setConvertOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Convert to Campaign</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Choose how this email is sent to the <strong>{rec.content_theme}</strong> audience.
          </Typography>

          <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2, mb: 2 }}>
            <Stack direction="row" alignItems="flex-start" spacing={1.5}>
              <Switch
                checked={personalised}
                onChange={(e) => setPersonalised(e.target.checked)}
                color="primary"
                sx={{ mt: 0.25, flexShrink: 0 }}
              />
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {personalised ? 'Personalised for each contact' : 'One size fits all'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {personalised
                    ? 'AI rewrites the greeting and opening for each recipient based on their travel interests.'
                    : 'Every contact receives exactly the same email.'}
                </Typography>
              </Box>
            </Stack>
          </Box>

          {personalised && (
            <Alert
              severity="info"
              icon={<AutoFixHighIcon fontSize="small" />}
              sx={{ fontSize: 12 }}
            >
              <strong>AI Personalisation uses Groq (fast, lightweight).</strong> Each recipient's
              greeting and opening line will be rewritten based on their destination interest,
              readiness score, and behaviour tags. The offer, pricing, and CTA remain identical
              for all recipients.
              <Box component="div" mt={1} sx={{ color: 'text.secondary', fontSize: 11 }}>
                A disclaimer will be added to the bottom of each personalised email stating that
                the content was tailored using AI.
              </Box>
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConvertOpen(false)} color="inherit">Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<RocketLaunchIcon />}
            onClick={handleConfirmConvert}
          >
            {personalised ? 'Convert with Personalisation' : 'Convert Campaign'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
